/**
 * Scorecard access: whose Google account Cora reads the scorecards with.
 *
 * The scorecards live in a shared drive that refuses outside accounts, so Cora reads them through a
 * person who can open the folder and connected their Google account here (read-only Docs). The panel
 * says plainly what that grants and whose it is, and anyone with Cora Operator access can reconnect
 * when the person leaves or the connection expires.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { connectCoraDocs, disconnectCoraDocs, fetchCoraDocsConnection } from '../../services/coraApi';
import { fmtWhen } from './coraFormat';
import { useCoraWrite } from './useCoraWrite';

const RETURN_ERRORS: Record<string, string> = {
  access_denied: 'The Google consent was cancelled.',
  wrong_account: 'Connect with the same Google account you are signed in with.',
  missing_scope: 'Google did not grant read access to Docs. Please allow it on the consent screen.',
  no_refresh_token: 'Google did not return a lasting connection. Please try again.',
  expired: 'The connection request expired. Please try again.',
};

export default function DocsConnection() {
  const queryClient = useQueryClient();
  const canWrite = useCoraWrite();
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const conn = useQuery({ queryKey: ['cora', 'docs-connection'], queryFn: fetchCoraDocsConnection, staleTime: 60_000 });

  // Back from Google's consent page: say how it went, then drop the flag from the URL.
  useEffect(() => {
    const ok = params.get('cora_docs');
    const err = params.get('cora_docs_error');
    if (!ok && !err) return;
    if (ok) {
      void logAnalyticsEvent('cora_docs_connected');
      showToast({ status: 'success', description: 'Connected — Cora reads the scorecards with your Google account from the next run.' });
    } else {
      void logAnalyticsEvent('cora_docs_connect_failed', { reason: err ?? 'unknown' });
      showToast({ status: 'error', description: RETURN_ERRORS[err ?? ''] ?? 'The Google connection failed. Please try again.' });
    }
    const next = new URLSearchParams(params);
    next.delete('cora_docs');
    next.delete('cora_docs_error');
    setParams(next, { replace: true });
    void queryClient.invalidateQueries({ queryKey: ['cora'] });
  }, [params, setParams, queryClient]);

  const connect = async () => {
    setBusy(true);
    void logAnalyticsEvent('cora_docs_connect_clicked');
    try {
      const { authorizeUrl } = await connectCoraDocs();
      window.location.assign(authorizeUrl);
    } catch (err) {
      setBusy(false);
      showToast({ status: 'error', description: err instanceof Error ? err.message : 'Could not start the Google connection.' });
    }
  };

  const disconnect = async () => {
    setBusy(true);
    void logAnalyticsEvent('cora_docs_disconnect_clicked');
    try {
      await disconnectCoraDocs();
      showToast({ status: 'success', description: 'Disconnected. Cora can no longer read the scorecards until someone reconnects.' });
      void queryClient.invalidateQueries({ queryKey: ['cora'] });
    } catch (err) {
      showToast({ status: 'error', description: err instanceof Error ? err.message : 'Could not disconnect.' });
    } finally {
      setBusy(false);
    }
  };

  const grant = conn.data?.grant;
  return (
    <section className="flex flex-col gap-2 rounded-sm border border-solid border-pearl p-4">
      <h2 className="text-sm font-semibold text-zinc-900">Scorecard access</h2>
      {conn.isLoading && <p className="text-sm text-zinc-600">Loading…</p>}
      {conn.isError && <p role="alert" className="text-sm text-red">The scorecard connection could not be loaded.</p>}
      {conn.data && grant && (
        <p className="text-sm text-zinc-700">
          Cora reads the scorecards with <strong>{grant.googleEmail}</strong>&apos;s Google account (read-only Docs), connected{' '}
          {fmtWhen(grant.connectedAt)}.{grant.lastError ? <span className="text-red"> Last error: {grant.lastError}</span> : null}
        </p>
      )}
      {conn.data && grant && !grant.canCreateFiles && (
        <p className="text-sm text-orange">
          Reconnect once so Kiki can create scorecard Docs — the new consent adds &quot;create files&quot;, which covers only files Cora
          and Kiki create themselves.
        </p>
      )}
      {conn.data && !grant && (
        <p className="text-sm text-zinc-700">
          No Google account is connected. Connect one that can open the scorecard folder (CV Screening › Kiki + Cora). Cora then
          reads Google Docs with it, read-only — she can open any Doc that account can open, and never edits one.
        </p>
      )}
      {conn.data && !conn.data.available && <p className="text-sm text-orange">The Google connection is not configured on this deployment.</p>}
      {conn.data?.available && canWrite && (
        <div className="flex gap-2">
          <Button variant="outline" onClick={connect} disabled={busy}>
            {grant ? 'Reconnect with my Google account' : 'Connect my Google account'}
          </Button>
          {grant && (
            <Button variant="ghost" onClick={disconnect} disabled={busy}>Disconnect</Button>
          )}
        </div>
      )}
      {conn.data?.available && !canWrite && <p className="text-xs text-zinc-500">Connecting needs Cora Operator access.</p>}
    </section>
  );
}
